/**
 * Turning an `ApiError` into something a student can read (spec §4.10).
 * The app is Swedish-only (2026-09-28): the server's message is written in
 * English for logs and developers, never for students, so it is never shown
 * as-is. Every known error code has a Swedish dictionary entry
 * (`client/src/i18n/sv.ts`); an error the client does not recognise falls
 * back to a generic Swedish message instead of leaking the raw server text.
 */
import { ApiError } from "@client/api/client";
import { ERROR_CODES } from "@shared/api";
import type { TFunction, TKey } from "@client/i18n";

const KNOWN_ERROR_CODES: readonly string[] = ERROR_CODES;

export function messageForError(error: unknown, t: TFunction): string {
  if (error instanceof ApiError) {
    if (error.status === 0) return t("error.offline");
    // `error.code` comes off the wire and is only *typed* as `ErrorCode`; an
    // unrecognised value (a future server bug, a proxy error page, …) must
    // still resolve to a Swedish sentence rather than the bare dictionary key.
    return KNOWN_ERROR_CODES.includes(error.code) ? t(`error.${error.code}`) : t("error.INTERNAL");
  }
  return t("error.INTERNAL");
}

/**
 * Which Swedish dictionary key a field's server-side issue maps to. Most of
 * these come from a zod issue (`registerSchema`/`loginSchema` in
 * `server/src/auth/routes.ts`), whose own English message is only used here
 * to tell the rules the client cares about apart (never shown). A wrong
 * registration/teacher code (review-O.md Minor 4) sends the field name alone
 * — `registrationCode`/`teacherCode` — as the signal, no message parsing
 * needed. Either way the result is always a dictionary key, never the
 * server's text.
 */
function fieldErrorKey(field: string, serverMessage: string): TKey {
  switch (field) {
    case "email":
      // Both the length check and the format check land here: either way
      // the fix is the same, a real email address.
      return "error.field.email.invalid";
    case "password":
      // Register's password rule carries this exact custom zod message;
      // login's (a bare "enter something") falls through to the generic key.
      return /at least 8 characters/.test(serverMessage)
        ? "error.field.password.min"
        : "error.field.generic";
    case "displayName":
      return "error.field.displayName.invalid";
    case "registrationCode":
      return "error.field.registrationCode.wrong";
    case "teacherCode":
      return "error.field.teacherCode.wrong";
    default:
      return "error.field.generic";
  }
}

/** Field → Swedish message map for inline form errors, empty when the error is global. */
export function fieldErrors(error: unknown, t: TFunction): Record<string, string> {
  if (!(error instanceof ApiError) || !error.fields) return {};
  const translated: Record<string, string> = {};
  for (const [field, serverMessage] of Object.entries(error.fields)) {
    translated[field] = t(fieldErrorKey(field, serverMessage));
  }
  return translated;
}

export function isCode(error: unknown, code: ApiError["code"]): boolean {
  return error instanceof ApiError && error.code === code;
}

/**
 * The N in a `RATE_LIMITED` error's "… Try again in N s." tail (both
 * `server/src/auth/authRateLimit.ts` and `server/src/auth/rateLimit.ts` end
 * their (English, log-only) message that way). Null for anything else, or if
 * the text does not carry the usual tail — callers then fall back to the
 * generic Swedish message with no number. Used to keep the retry timing in a
 * page-local Swedish override (review-O.md Minor 4) without ever showing the
 * server's own text.
 */
export function rateLimitRetrySeconds(error: unknown): number | null {
  if (!(error instanceof ApiError) || error.code !== "RATE_LIMITED") return null;
  const match = /Try again in (\d+) s\.$/.exec(error.message);
  return match ? Number(match[1]) : null;
}
